import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Building2, Users, MapPin, ClipboardList, UserCheck } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const Bar = ({ pct, color }) => (
  <div className="w-full h-2 bg-slate-200 rounded-full overflow-hidden mt-1">
    <div className={`h-full ${color} rounded-full transition-all`} style={{ width: `${Math.min(pct, 100)}%` }} />
  </div>
);

const Metric = ({ label, value }) => (
  <div>
    <div className="text-[10px] uppercase text-slate-400 font-medium">{label}</div>
    <div className="text-sm font-bold text-slate-800">{value}</div>
  </div>
);

const FunnelRow = ({ label, count, pct, color }) => (
  <div className="mb-2" data-testid={`recep-funnel-${label.replace(/\s+/g, '-').toLowerCase()}`}>
    <div className="text-[11px] font-semibold text-slate-700 flex justify-between">
      <span>{label}</span>
      <span>{(count || 0).toLocaleString()} · {pct}%</span>
    </div>
    <Bar pct={pct} color={color} />
  </div>
);

const STATUS_COLORS = {
  DONE: 'bg-emerald-500', INPROCESS: 'bg-amber-400', HOLD: 'bg-sky-500', CANCEL: 'bg-rose-500',
};

export const CMDReceptionPanel = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    axios.get(`${API}/receptionist/cmd-overview`)
      .then(r => setData(r.data))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  if (loading) return <div className="py-8 text-center text-slate-400 text-sm" data-testid="cmd-reception-loading">Loading Reception summary…</div>;
  if (!data) return null;

  const { officeVisits, leads, attendance } = data;
  const statusEntries = Object.entries(leads.statusBreakdown || {}).sort((a, b) => b[1].count - a[1].count);

  return (
    <Card className="border-0 shadow-md" data-testid="cmd-reception-panel">
      <CardHeader className="pb-3">
        <CardTitle className="text-base font-bold flex items-center gap-2">
          <Building2 className="w-4 h-4 text-indigo-600" />
          Reception Snapshot — Hetal Mandalik (%-wise)
        </CardTitle>
      </CardHeader>
      <CardContent>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {/* Lead Funnel */}
          <div className="p-3 rounded-lg bg-indigo-50 border border-indigo-100" data-testid="cmd-reception-leads">
            <div className="flex items-center gap-2 mb-2"><ClipboardList className="w-4 h-4 text-indigo-600" /><span className="font-bold text-sm text-indigo-900">Lead Funnel</span></div>
            <div className="mb-2"><Metric label="Total Leads" value={(leads.total || 0).toLocaleString()} /></div>
            <FunnelRow label="Office Visit" count={leads.officeVisit.count} pct={leads.officeVisit.pct} color="bg-indigo-500" />
            <FunnelRow label="Project Visit" count={leads.projectVisit.count} pct={leads.projectVisit.pct} color="bg-violet-500" />
            <FunnelRow label="Booking" count={leads.booking.count} pct={leads.booking.pct} color="bg-emerald-500" />
            <FunnelRow label="Confirm Sale" count={leads.confirmSale.count} pct={leads.confirmSale.pct} color="bg-teal-600" />
          </div>

          {/* Lead Status */}
          <div className="p-3 rounded-lg bg-slate-50 border border-slate-200" data-testid="cmd-reception-status">
            <div className="flex items-center gap-2 mb-2"><MapPin className="w-4 h-4 text-slate-600" /><span className="font-bold text-sm text-slate-800">Lead Status (%)</span></div>
            {statusEntries.length === 0 && <div className="text-xs text-slate-400">No status data</div>}
            {statusEntries.map(([st, v]) => (
              <div key={st} className="mb-2">
                <div className="text-[11px] font-semibold text-slate-700 flex justify-between">
                  <span>{st}</span><span>{(v.count || 0).toLocaleString()} · {v.pct}%</span>
                </div>
                <Bar pct={v.pct} color={STATUS_COLORS[st] || 'bg-slate-400'} />
              </div>
            ))}
          </div>

          {/* Office Visits + Attendance */}
          <div className="p-3 rounded-lg bg-blue-50 border border-blue-100" data-testid="cmd-reception-visits">
            <div className="flex items-center gap-2 mb-2"><Users className="w-4 h-4 text-blue-600" /><span className="font-bold text-sm text-blue-900">Office Visits & Staff</span></div>
            <div className="grid grid-cols-2 gap-2 mb-3">
              <Metric label="Total Visits" value={(officeVisits.total || 0).toLocaleString()} />
              <Metric label="Active DMs" value={officeVisits.uniqueDMs} />
              <Metric label="Top CP Area" value={officeVisits.topCP} />
              <Metric label="Top CP Share" value={`${officeVisits.topCPShare}%`} />
            </div>
            <div className="text-[11px] font-semibold text-blue-800 flex justify-between items-center">
              <span className="flex items-center gap-1"><UserCheck className="w-3 h-3" /> Presenty Confirmed</span>
              <span>{attendance.presentPct}%</span>
            </div>
            <Bar pct={attendance.presentPct} color="bg-blue-500" />
            <div className="text-[10px] text-slate-500 mt-2">
              {attendance.presentyTotal} presenty records · {attendance.lateMarks} late marks logged
            </div>
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

export default CMDReceptionPanel;
